import { useMemo, useState, useEffect, useCallback } from "react";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import {
  Loader,
  MoreVertical,
  PiggyBank,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  HandCoins,
  Eye,
  Trash2,
  Search,
  Calendar as CalendarIcon,
  Download,
  Printer,
  FileText,
  AlertTriangle,
  Ban,
  CheckCheck,
  UserCheck,
  Banknote,
  CreditCard,
  Smartphone,
  Building,
  Landmark,
  Plus,
  Upload,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatCurrency } from "@/lib/utils";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { useAuth } from "@/context/auth.context";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";

function SavingDetailsDialog({ isOpen, setIsOpen, saving }) {
  if (!saving) return null;
  const teacher = saving.teacher || {};
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#0A9F43]" />
            Saving Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="flex items-center gap-4 rounded-lg bg-gray-50 p-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-[#0A9F43]/10 text-[#0A9F43] text-sm font-bold">
                {teacher.firstName?.[0]}{teacher.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {teacher.firstName} {teacher.lastName}
              </h3>
              <p className="text-sm text-gray-500">ID: {teacher.teacherId || teacher.id}</p>
              <p className="text-sm text-gray-500">{teacher.phone || ""} {teacher.email ? `| ${teacher.email}` : ""}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Amount</Label>
              <p className="text-lg font-bold text-[#0A9F43]">{formatCurrency(saving.amount || 0)}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Status</Label>
              <div className="mt-1">
                <StatusBadge status={saving.status} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Saving Date</Label>
              <p className="font-medium">{saving.contributionDate ? format(new Date(saving.contributionDate), "dd MMM yyyy") : "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Salary Month</Label>
              <p className="font-medium">{saving.salaryMonth ? format(new Date(saving.salaryMonth), "MMM yyyy") : "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Type</Label>
              <p className="font-medium">{saving.contributionType || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Method</Label>
              <p className="font-medium">{saving.paymentMethod || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Source</Label>
              <p className="font-medium capitalize">{saving.source?.replace(/_/g, " ") || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Verified By</Label>
              <p className="font-medium">{saving.verifiedBy || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Reference</Label>
              <p className="font-medium">{saving.referenceNumber || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Created At</Label>
              <p className="font-medium">{saving.createdAt ? format(new Date(saving.createdAt), "dd MMM yyyy HH:mm") : "N/A"}</p>
            </div>
          </div>
          {saving.notes && (
            <div>
              <Label className="text-xs text-gray-500">Notes</Label>
              <p className="mt-1 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{saving.notes}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: string }) {
  const norm = (status || "").toLowerCase();
  const styles: Record<string, string> = {
    verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    deducted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    declared: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
    cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  };
  let match = Object.keys(styles).find((k) => norm.includes(k));
  if (!match) match = "pending";
  return (
    <Badge variant="outline" className={`text-xs font-semibold px-2.5 py-0.5 rounded-md ${styles[match]}`}>
      {status || "Pending"}
    </Badge>
  );
}

const statusOptions = [
  { label: "Verified", value: "Verified" },
  { label: "Pending", value: "Pending" },
  { label: "Declared", value: "Declared" },
  { label: "Completed", value: "Completed" },
  { label: "Rejected", value: "Rejected" },
  { label: "Cancelled", value: "Cancelled" },
];

const methodOptions = [
  { label: "Cash", value: "Cash" },
  { label: "Bank Transfer", value: "Bank Transfer" },
  { label: "Mobile Money", value: "Mobile Money" },
  { label: "Card", value: "Card" },
  { label: "Salary Deduction", value: "salary_deduction" },
];

const typeOptions = [
  { label: "Monthly", value: "Monthly" },
  { label: "One-time", value: "One-time" },
  { label: "Weekly", value: "Weekly" },
];

const monthOptions = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function MethodIcon({ method }: { method?: string }) {
  const m = (method || "").toLowerCase();
  if (m.includes("cash")) return <Banknote className="h-3.5 w-3.5 text-green-600" />;
  if (m.includes("transfer") || m.includes("bank")) return <Landmark className="h-3.5 w-3.5 text-blue-600" />;
  if (m.includes("mobile") || m.includes("momo")) return <Smartphone className="h-3.5 w-3.5 text-purple-600" />;
  if (m.includes("card")) return <CreditCard className="h-3.5 w-3.5 text-indigo-600" />;
  if (m.includes("salary") || m.includes("deduction")) return <Building className="h-3.5 w-3.5 text-amber-600" />;
  return <Banknote className="h-3.5 w-3.5 text-gray-400" />;
}

function RecordSavingDialog({
  isOpen,
  setIsOpen,
  onSuccess,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSuccess: () => void;
}) {
  const [memberSearch, setMemberSearch] = useState("");
  const [members, setMembers] = useState<any[]>([]);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [memberPopoverOpen, setMemberPopoverOpen] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [savingDate, setSavingDate] = useState<Date>(new Date());
  const [contributionType, setContributionType] = useState("Monthly");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [contributionStatus, setContributionStatus] = useState("Verified");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchMembers = useCallback(async (search: string) => {
    setMembersLoading(true);
    setMembersError("");
    try {
      const params: Record<string, any> = {
        limit: 20,
        page: 1,
        sortBy: "fullNames",
        order: "ASC",
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      const { data } = await api.get("/members", { params });
      const items = data?.results || data?.data || [];
      setMembers(items);
    } catch (err: any) {
      setMembersError(err?.response?.data?.message || "Failed to load members");
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setMemberSearch("");
      setSelectedMember(null);
      setAmount("");
      setNotes("");
      setFile(null);
      setSavingDate(new Date());
      setContributionType("Monthly");
      setPaymentMethod("");
      setContributionStatus("Verified");
      setErrors({});
      fetchMembers("");
    }
  }, [isOpen, fetchMembers]);

  useEffect(() => {
    if (!memberSearch || memberSearch.length < 1) {
      if (!selectedMember) fetchMembers("");
      return;
    }
    const timer = setTimeout(() => {
      fetchMembers(memberSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, fetchMembers, selectedMember]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!selectedMember) errs.member = "Please select a member";
    if (!savingDate) errs.date = "Please select a saving date";
    if (!contributionType) errs.type = "Please select a type";
    if (!paymentMethod) errs.method = "Please select a payment method";
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) errs.amount = "Please enter a valid amount";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("teacherId", String(selectedMember.id));
      formData.append("amount", amount);
      formData.append("contributionDate", format(savingDate, "yyyy-MM-dd"));
      formData.append("contributionType", contributionType);
      formData.append("paymentMethod", paymentMethod);
      formData.append("status", contributionStatus);
      formData.append("source", paymentMethod === "Salary Deduction" || paymentMethod === "Payroll Deduction" ? "salary_deduction" : "manual_member_contribution");
      if (notes) formData.append("notes", notes);
      if (file) formData.append("documentReceipt", file);

      await api.post("/contributions", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Saving recorded successfully");
      setIsOpen(false);
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to record saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[720px] !max-w-[720px] max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Plus className="h-5 w-5 text-[#0A9F43]" /> Record Member Saving
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Fill in the details to record a new savings contribution.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4 max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Member <span className="text-red-500">*</span></Label>
              <Popover open={memberPopoverOpen} onOpenChange={setMemberPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={memberPopoverOpen}
                    className="w-full justify-between h-10 text-sm font-normal"
                  >
                    {selectedMember ? (
                      <div className="flex items-center gap-2 truncate">
                        <Avatar className="h-6 w-6 flex-shrink-0">
                          <AvatarFallback className="text-[10px] bg-[#0A9F43]/10 text-[#0A9F43] font-bold">
                            {(selectedMember.fullNames || selectedMember.firstName + " " + selectedMember.lastName || "??").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{selectedMember.fullNames || `${selectedMember.firstName || ""} ${selectedMember.lastName || ""}`.trim()}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">({selectedMember.memberCode || `#${selectedMember.id}`})</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">Search member by name, ID, phone, email or national ID...</span>
                    )}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[658px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search by name, member ID, phone, email or national ID..."
                      value={memberSearch}
                      onValueChange={setMemberSearch}
                    />
                    {membersLoading ? (
                      <div className="flex items-center justify-center py-8 text-sm text-gray-400">
                        <Loader className="h-4 w-4 animate-spin mr-2" /> Loading members...
                      </div>
                    ) : membersError ? (
                      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                        <AlertTriangle className="h-6 w-6 text-red-400 mb-2" />
                        <p className="text-sm text-red-500">{membersError}</p>
                        <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={() => fetchMembers(memberSearch)}>
                          Retry
                        </Button>
                      </div>
                    ) : members.length === 0 && !membersLoading && !membersError ? (
                      <CommandEmpty>
                        {memberSearch.trim() ? "No members match your search." : "No members found."}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup className="max-h-[220px] overflow-y-auto">
                        {members.map((m: any) => (
                          <CommandItem
                            key={m.id}
                            value={String(m.id)}
                            onSelect={() => {
                              setSelectedMember(m);
                              setMemberPopoverOpen(false);
                              setMemberSearch("");
                            }}
                          >
                            <div className="flex items-center gap-3 w-full">
                              <Avatar className="h-8 w-8 flex-shrink-0">
                                <AvatarFallback className="text-xs bg-[#0A9F43]/10 text-[#0A9F43] font-bold">
                                  {(m.fullNames || m.firstName + " " + m.lastName || "??").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{m.fullNames || `${m.firstName || ""} ${m.lastName || ""}`.trim()}</p>
                                <p className="text-xs text-gray-400 truncate">
                                  {m.memberCode && <span className="font-mono">{m.memberCode}</span>}
                                  {m.memberCode && m.phone && <span> | </span>}
                                  {m.phone && <span>{m.phone}</span>}
                                  {(m.memberCode || m.phone) && m.email && <span> | </span>}
                                  {m.email && <span className="truncate">{m.email}</span>}
                                </p>
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.member && <p className="text-xs text-red-500">{errors.member}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Saving Date <span className="text-red-500">*</span></Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-10 text-sm"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {savingDate ? format(savingDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={savingDate}
                    onSelect={(d) => d && setSavingDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.date && <p className="text-xs text-red-500">{errors.date}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Type <span className="text-red-500">*</span></Label>
              <Select value={contributionType} onValueChange={setContributionType}>
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.type && <p className="text-xs text-red-500">{errors.type}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Method <span className="text-red-500">*</span></Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Salary Deduction">Salary Deduction</SelectItem>
                  <SelectItem value="Payroll Deduction">Payroll Deduction</SelectItem>
                  <SelectItem value="Manual Payment">Manual Payment</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
              {errors.method && <p className="text-xs text-red-500">{errors.method}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Amount <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-10 text-sm pl-16"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">FRW</span>
              </div>
              {errors.amount && <p className="text-xs text-red-500">{errors.amount}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={contributionStatus} onValueChange={setContributionStatus}>
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Verified">Verified / Approved</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label>Notes <span className="text-gray-400">(optional)</span></Label>
              <Textarea
                placeholder="Enter any notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[60px] text-sm"
              />
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label>Attachment/Proof <span className="text-gray-400">(optional)</span></Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-xs"
                  onClick={() => document.getElementById("receipt-upload")?.click()}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {file ? "Change File" : "Upload Receipt"}
                </Button>
                <input
                  id="receipt-upload"
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                {file && (
                  <span className="text-xs text-gray-500 truncate max-w-[300px]">{file.name}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t gap-2">
          <Button variant="outline" size="sm" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="sm" className="bg-[#0A9F43] hover:bg-[#087a34]" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Saving"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Savings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.isAdmin;
  const isLeader = isAdmin || ["President", "Secretary", "Accountant"].includes(user?.role?.name || user?.leadershipRole || "");

  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({
    from: subMonths(startOfMonth(new Date()), 6),
    to: endOfMonth(new Date()),
  });
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [viewSaving, setViewSaving] = useState<any>(null);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; saving: any; reason: string }>({
    open: false, saving: null, reason: "",
  });
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);

  const availableYears = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => y - i);
  }, []);

  const queryFilters = useMemo(() => {
    const f: any[] = [];
    if (filterStatus !== "all") f.push({ field: "status", operator: "eq", value: filterStatus });
    if (filterMethod !== "all") f.push({ field: "paymentMethod", operator: "eq", value: filterMethod });
    if (filterMonth !== "all") f.push({ field: "month", operator: "eq", value: parseInt(filterMonth) });
    if (filterYear !== "all") f.push({ field: "year", operator: "eq", value: parseInt(filterYear) });
    return f;
  }, [filterStatus, filterMethod, filterMonth, filterYear]);

  const recordsQuery = useQuery({
    queryKey: ["savings", searchText, pageIndex, pageSize, sorting, queryFilters, dateRange],
    keepPreviousData: true,
    queryFn: async () => {
      const params: Record<string, any> = {
        limit: pageSize,
        page: pageIndex + 1,
        sortBy: sorting[0]?.id || "createdAt",
        order: sorting[0]?.desc ? "DESC" : "ASC",
      };
      if (searchText) params.search = searchText;
      if (queryFilters.length > 0) params.filters = JSON.stringify(queryFilters);
      if (dateRange?.from) params.start_date = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.end_date = format(dateRange.to, "yyyy-MM-dd");

      const { data } = await api.get("/contributions", { params });
      return data;
    },
  });

  const statsQuery = useQuery({
    queryKey: ["savings-stats", queryFilters, dateRange],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 10000 };
      if (queryFilters.length > 0) params.filters = JSON.stringify(queryFilters);
      if (dateRange?.from) params.start_date = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.end_date = format(dateRange.to, "yyyy-MM-dd");
      const { data } = await api.get("/contributions", { params });
      const items = data?.results || [];
      const total = items.length;
      const totalAmount = items.reduce((s: number, c: any) => s + Number(c.amount || 0), 0);
      const approved = items.filter((c: any) => {
        const s = (c.status || "").toLowerCase();
        return s === "verified" || s === "completed" || s === "deducted from salary" || s === "approved by president" || s === "confirmed by accountant";
      });
      const pending = items.filter((c: any) => {
        const s = (c.status || "").toLowerCase();
        return s === "pending" || s === "declared" || s === "pending verification";
      });
      const rejected = items.filter((c: any) => (c.status || "").toLowerCase() === "rejected");
      const payroll = items.filter((c: any) => c.source === "salary_deduction");
      const manual = items.filter((c: any) => c.source === "manual_member_contribution" || !c.source);

      return {
        totalSavings: totalAmount,
        approvedAmount: approved.reduce((s: number, c: any) => s + Number(c.amount || 0), 0),
        pendingAmount: pending.reduce((s: number, c: any) => s + Number(c.amount || 0), 0),
        rejectedAmount: rejected.reduce((s: number, c: any) => s + Number(c.amount || 0), 0),
        payrollAmount: payroll.reduce((s: number, c: any) => s + Number(c.amount || 0), 0),
        manualAmount: manual.reduce((s: number, c: any) => s + Number(c.amount || 0), 0),
        totalCount: total,
        approvedCount: approved.length,
        pendingCount: pending.length,
        rejectedCount: rejected.length,
      };
    },
    staleTime: 15000,
    retry: 1,
  });

  const verifyMutation = useMutation(
    async (id: number) => {
      const { data } = await api.patch(`/contributions/${id}/verify`);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Saving verified successfully");
        queryClient.invalidateQueries(["savings"]);
        queryClient.invalidateQueries(["savings-stats"]);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to verify saving");
      },
    }
  );

  const rejectMutation = useMutation(
    async ({ id, reason }: { id: number; reason: string }) => {
      const { data } = await api.patch(`/contributions/${id}/reject`, { reason });
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Saving rejected");
        setRejectDialog({ open: false, saving: null, reason: "" });
        queryClient.invalidateQueries(["savings"]);
        queryClient.invalidateQueries(["savings-stats"]);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to reject saving");
      },
    }
  );

  const deleteMutation = useMutation(
    async (id: number) => {
      const { data } = await api.delete(`/contributions/${id}`);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Saving deleted successfully");
        queryClient.invalidateQueries(["savings"]);
        queryClient.invalidateQueries(["savings-stats"]);
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to delete saving");
      },
    }
  );

  const handleRecordSuccess = () => {
    queryClient.invalidateQueries(["savings"]);
    queryClient.invalidateQueries(["savings-stats"]);
  };

  const stats = statsQuery.data || {
    totalSavings: 0, approvedAmount: 0, pendingAmount: 0, rejectedAmount: 0,
    payrollAmount: 0, manualAmount: 0, totalCount: 0, approvedCount: 0,
    pendingCount: 0, rejectedCount: 0,
  };

  const contributions = recordsQuery.data?.results || [];
  const loading = recordsQuery.status === "loading";
  const statsLoading = statsQuery.status === "loading";

  const formatDate = (d: any) => d ? format(new Date(d), "dd MMM yyyy") : "N/A";
  const formatDateTime = (d: any) => d ? format(new Date(d), "dd MMM yyyy HH:mm") : "N/A";

  const columns: ColumnDef<any>[] = [
    {
      id: "sn",
      header: ({ column }) => <DataTableColumnHeader column={column} title="S/N" />,
      cell: ({ row }) => <span className="text-xs text-gray-500">{row.index + 1 + pageIndex * pageSize}</span>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "teacher.teacherId",
      id: "memberId",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member ID" />,
      cell: ({ row }) => {
        const teacher = row.original.teacher || {};
        return (
          <span className="font-mono text-xs font-semibold text-slate-700">
            {teacher.teacherId || `T-${String(teacher.id).padStart(4, "0")}`}
          </span>
        );
      },
      enableSorting: true,
    },
    {
      id: "memberName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member Name" />,
      cell: ({ row }) => {
        const teacher = row.original.teacher || {};
        return (
          <div className="flex items-center gap-2 min-w-[180px]">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-[#0A9F43]/10 text-[#0A9F43] text-[10px] font-bold">
                {teacher.firstName?.[0]}{teacher.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="leading-tight">
              <p className="text-sm font-medium text-gray-900">{teacher.fullName || `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim()}</p>
              <p className="text-[10px] text-gray-400">{teacher.phone || teacher.email || ""}</p>
            </div>
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "contributionDate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Saving Date" />,
      cell: ({ row }) => (
        <span className="text-sm text-slate-600 whitespace-nowrap">{formatDate(row.original.contributionDate)}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "contributionType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs font-medium bg-slate-50 text-slate-700 border-slate-200">
          {row.original.contributionType || "N/A"}
        </Badge>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "paymentMethod",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 min-w-[120px]">
          <MethodIcon method={row.original.paymentMethod} />
          <span className="text-sm text-slate-600">{row.original.paymentMethod || "N/A"}</span>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => (
        <span className="text-sm font-bold text-[#0A9F43] whitespace-nowrap">{formatCurrency(row.original.amount || 0)}</span>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      enableSorting: true,
    },
    {
      id: "approvedBy",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Approved By" />,
      cell: ({ row }) => {
        const approvedBy = row.original.verifiedBy || row.original.hrApprovedBy || row.original.confirmedBy || "";
        return (
          <span className="text-sm text-slate-600">{approvedBy || <span className="text-gray-400 italic text-xs">Pending</span>}</span>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created At" />,
      cell: ({ row }) => (
        <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(row.original.createdAt)}</span>
      ),
      enableSorting: true,
    },
    {
      id: "actions",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Actions" />,
      cell: ({ row }) => {
        const s = row.original;
        const status = (s.status || "").toLowerCase();
        const isPending = status === "pending" || status === "declared" || status === "pending verification";
        return (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setViewSaving(s)}>
              <Eye className="h-4 w-4 text-slate-500" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setViewSaving(s)}>
                  <Eye className="mr-2 h-4 w-4" /> View Details
                </DropdownMenuItem>
                {isPending && isLeader && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => verifyMutation.mutate(s.id)} className="text-emerald-600">
                      <CheckCheck className="mr-2 h-4 w-4" /> Verify / Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setRejectDialog({ open: true, saving: s, reason: "" })} className="text-red-600">
                      <Ban className="mr-2 h-4 w-4" /> Reject
                    </DropdownMenuItem>
                  </>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/members/${s.teacher?.id}?tab=contributions`)}>
                      <UserCheck className="mr-2 h-4 w-4" /> View Member
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteMutation.mutate(s.id)} className="text-red-600">
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableSorting: false,
    },
  ];

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    setDateRange(range || { from: undefined, to: undefined });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleFilterChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleRefresh = () => {
    recordsQuery.refetch();
    statsQuery.refetch();
    toast.success("Refreshed savings data");
  };

  const downloadCsv = () => {
    const headers = ["S/N", "Member ID", "Member Name", "Saving Date", "Type", "Method", "Amount", "Status", "Approved By", "Created At"];
    const rows = contributions.map((c: any, i: number) => {
      const t = c.teacher || {};
      return [
        i + 1 + pageIndex * pageSize,
        t.teacherId || `T-${String(t.id).padStart(4, "0")}`,
        t.fullName || `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        c.contributionDate ? format(new Date(c.contributionDate), "yyyy-MM-dd") : "",
        c.contributionType || "",
        c.paymentMethod || "",
        c.amount || 0,
        c.status || "",
        c.verifiedBy || "",
        c.createdAt ? format(new Date(c.createdAt), "yyyy-MM-dd HH:mm") : "",
      ];
    });
    const csv = [headers.join(","), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `savings-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printContent = `
      <html><head>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; color: #050500; margin-bottom: 5px; }
          .subtitle { font-size: 12px; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background-color: #0A9F43; color: white; padding: 8px 6px; text-align: left; border: 1px solid #087a34; }
          td { border: 1px solid #ddd; padding: 6px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary { display: flex; gap: 20px; margin-bottom: 20px; flex-wrap: wrap; }
          .summary-item { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 16px; min-width: 120px; }
          .summary-item .label { font-size: 11px; color: #666; }
          .summary-item .value { font-size: 16px; font-weight: bold; color: #050500; }
          @media print { @page { margin: 0.5cm; } }
        </style>
      </head><body>
        <h1>Members Savings</h1>
        <p class="subtitle">Generated on ${format(new Date(), "MMMM d, yyyy")}</p>
        <div class="summary">
          <div class="summary-item"><div class="label">Total Savings</div><div class="value">${formatCurrency(stats.totalSavings)}</div></div>
          <div class="summary-item"><div class="label">Approved</div><div class="value">${formatCurrency(stats.approvedAmount)}</div></div>
          <div class="summary-item"><div class="label">Pending</div><div class="value">${formatCurrency(stats.pendingAmount)}</div></div>
          <div class="summary-item"><div class="label">Rejected</div><div class="value">${formatCurrency(stats.rejectedAmount)}</div></div>
        </div>
        <table>
          <thead><tr>
            <th>S/N</th><th>Member ID</th><th>Member Name</th><th>Saving Date</th><th>Type</th><th>Method</th>
            <th>Amount</th><th>Status</th><th>Approved By</th><th>Created At</th>
          </tr></thead>
          <tbody>
            ${contributions.map((c: any, i: number) => {
              const t = c.teacher || {};
              const name = t.fullName || `${t.firstName || ""} ${t.lastName || ""}`.trim();
              const id = t.teacherId || `T-${String(t.id).padStart(4, "0")}`;
              return `<tr>
                <td>${i + 1 + pageIndex * pageSize}</td>
                <td>${id}</td>
                <td>${name}</td>
                <td>${c.contributionDate ? format(new Date(c.contributionDate), "dd MMM yyyy") : ""}</td>
                <td>${c.contributionType || ""}</td>
                <td>${c.paymentMethod || ""}</td>
                <td style="text-align:right">${(c.amount || 0).toLocaleString()} FRW</td>
                <td>${c.status || "Pending"}</td>
                <td>${c.verifiedBy || ""}</td>
                <td>${c.createdAt ? format(new Date(c.createdAt), "dd MMM yyyy") : ""}</td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(printContent);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); }, 500);
    }
  };

  const summaryCards = [
    { title: "Total Savings", value: stats.totalSavings, icon: PiggyBank, color: "green", loading: statsLoading },
    { title: "Approved", value: stats.approvedAmount, icon: CheckCircle2, color: "green", loading: statsLoading, sub: `${stats.approvedCount} records` },
    { title: "Pending", value: stats.pendingAmount, icon: Clock, color: "amber", loading: statsLoading, sub: `${stats.pendingCount} records` },
    { title: "Rejected", value: stats.rejectedAmount, icon: XCircle, color: "red", loading: statsLoading, sub: `${stats.rejectedCount} records` },
    { title: "Payroll Deducted", value: stats.payrollAmount, icon: Building, color: "blue", loading: statsLoading },
    { title: "Manual Savings", value: stats.manualAmount, icon: HandCoins, color: "purple", loading: statsLoading },
  ];

  return (
    <div className="w-full max-w-full px-4 py-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A9F43]/10">
            <PiggyBank className="h-5 w-5 text-[#0A9F43]" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Members Savings</h1>
            <p className="text-xs text-gray-400">Track and manage all member savings contributions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLeader && (
            <Button variant="default" size="sm" className="h-8 text-xs bg-[#0A9F43] hover:bg-[#087a34]" onClick={() => setRecordDialogOpen(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Record Saving
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handlePrint}>
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadCsv}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleRefresh} disabled={loading || statsLoading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {summaryCards.map((card) => (
          <Card key={card.title} className="relative overflow-hidden border-gray-100 shadow-sm">
            <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-${card.color}-500 to-${card.color}-400`} />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold text-gray-600">{card.title}</p>
                <div className={`flex h-6 w-6 items-center justify-center rounded-full bg-${card.color}-50 text-${card.color}-600`}>
                  <card.icon className="h-3.5 w-3.5" />
                </div>
              </div>
              {card.loading ? (
                <div className="h-5 w-20 animate-pulse rounded bg-gray-200" />
              ) : (
                <p className={`text-base font-bold text-${card.color}-700`}>{formatCurrency(card.value)}</p>
              )}
              {card.sub && !card.loading && (
                <p className="text-[10px] text-gray-400 mt-0.5">{card.sub}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-0 sm:max-w-[300px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search by name, ID, phone, email..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
            className="h-8 pl-8 text-xs"
          />
        </div>
        <Select value={filterStatus} onValueChange={handleFilterChange(setFilterStatus)}>
          <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statusOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMethod} onValueChange={handleFilterChange(setFilterMethod)}>
          <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue placeholder="Method" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            {methodOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={handleFilterChange(setFilterMonth)}>
          <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {monthOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={handleFilterChange(setFilterYear)}>
          <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("h-8 text-xs", dateRange?.from ? "text-gray-900" : "text-gray-400")}>
              <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
              {dateRange?.from ? `${format(dateRange.from, "dd/MM/yy")} - ${format(dateRange.to || new Date(), "dd/MM/yy")}` : "Date Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={{ from: dateRange?.from, to: dateRange?.to }}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
        {(filterStatus !== "all" || filterMethod !== "all" || filterMonth !== "all" || filterYear !== "all") && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500"
            onClick={() => { setFilterStatus("all"); setFilterMethod("all"); setFilterMonth("all"); setFilterYear("all"); }}>
            Clear Filters
          </Button>
        )}
      </div>

      {/* Error State */}
      {recordsQuery.status === "error" && (
        <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-red-200 bg-red-50 mb-3">
          <div className="text-center p-6">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-red-400" />
            <h3 className="text-sm font-semibold text-red-700 mb-1">Failed to load savings</h3>
            <p className="text-xs text-red-500 mb-3">{(recordsQuery.error as any)?.response?.data?.message || "An error occurred while fetching data."}</p>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => recordsQuery.refetch()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        </div>
      )}

      {recordsQuery.status !== "error" && (
        <DataTable
          columns={columns}
          data={contributions}
          isLoading={loading}
          isFetching={recordsQuery.isFetching}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={recordsQuery.data?.totalPages || 0}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          onSearch={(v) => { setSearchText(v); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
          enableClientFiltering={false}
          emptyMessage="No savings records found."
          emptyDescription="Savings contributions will appear here once recorded."
          hidePagination={false}
          showSearch={false}
          facets={[
            { name: "status", title: "Status", type: "select", options: statusOptions },
            { name: "contributionType", title: "Type", type: "select", options: typeOptions },
          ]}
        />
      )}

      {/* View Details Dialog */}
      <SavingDetailsDialog isOpen={!!viewSaving} setIsOpen={(open) => { if (!open) setViewSaving(null); }} saving={viewSaving} />

      {/* Record Saving Dialog */}
      <RecordSavingDialog isOpen={recordDialogOpen} setIsOpen={setRecordDialogOpen} onSuccess={handleRecordSuccess} />

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) setRejectDialog({ open: false, saving: null, reason: "" }); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" /> Reject Saving
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this saving contribution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">
                {rejectDialog.saving?.teacher?.firstName} {rejectDialog.saving?.teacher?.lastName}
              </p>
              <p className="text-xs text-gray-500">{formatCurrency(rejectDialog.saving?.amount || 0)}</p>
            </div>
            <div>
              <Label>Reason for rejection</Label>
              <Textarea
                placeholder="Enter reason..."
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
                className="min-h-[80px] mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRejectDialog({ open: false, saving: null, reason: "" })}>Cancel</Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              disabled={!rejectDialog.reason.trim() || rejectMutation.isLoading}
              onClick={() => rejectDialog.saving && rejectMutation.mutate({ id: rejectDialog.saving.id, reason: rejectDialog.reason })}
            >
              {rejectMutation.isLoading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
